import { routes } from "@/router/all_routes";
import {
  getStorageItem,
  setStorageItem,
  removeStorageItem,
} from "@/core/tenant/hospitol-storage";

const STORAGE_KEY = "hospitol.demoMenuHidden";
const VISIBILITY_EVENT = "hospitol-menu-visibility";

/** Toujours visible — accès à la configuration démo. */
export const MENU_DEMO_ADMIN_PATH = routes.adminMenuDemo;

export function getHiddenMenuPaths(): Set<string> {
  try {
    const raw = getStorageItem(STORAGE_KEY);
    if (!raw) return new Set();
    const list = JSON.parse(raw) as string[];
    return new Set(Array.isArray(list) ? list : []);
  } catch {
    return new Set();
  }
}

export function setHiddenMenuPaths(paths: string[]): void {
  const unique = [...new Set(paths)].filter((p) => p !== MENU_DEMO_ADMIN_PATH);
  setStorageItem(STORAGE_KEY, JSON.stringify(unique));
  window.dispatchEvent(new CustomEvent(VISIBILITY_EVENT));
}

export function clearHiddenMenuPaths(): void {
  removeStorageItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(VISIBILITY_EVENT));
}

export function isMenuPathHidden(path: string): boolean {
  if (path === MENU_DEMO_ADMIN_PATH) return false;
  return getHiddenMenuPaths().has(path);
}

export function subscribeMenuVisibility(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(VISIBILITY_EVENT, handler);
  return () => window.removeEventListener(VISIBILITY_EVENT, handler);
}

export function getMenuVisibilitySnapshot(): string {
  return getStorageItem(STORAGE_KEY) ?? "[]";
}

export interface MenuCatalogItem {
  path: string;
  label: string;
  badge?: "soon" | "live";
}

export interface MenuCatalogSection {
  id: string;
  title: string;
  items: MenuCatalogItem[];
}

/** Presets démo rapides */
export const MENU_DEMO_PRESETS = {
  all: { label: "Tout afficher", hidden: [] as string[] },
  pharmacy: {
    label: "Pharmacie uniquement",
    hidden: [
      routes.admissions,
      routes.emergency,
      routes.appointments,
      routes.patients,
      routes.patientRecords,
      routes.consultations,
      routes.prescriptions,
      routes.laboratory,
      routes.labResults,
      routes.imaging,
      routes.imagingReports,
      routes.rhEmployees,
      routes.rhDepartments,
      routes.rhSchedules,
      routes.supplyMedicinesArticles,
      routes.supplyMedicinesLevels,
      routes.supplyMedicinesReceipts,
      routes.supplyConsumablesArticles,
      routes.supplyConsumablesLevels,
      routes.supplyConsumablesReceipts,
      routes.adminUsers,
      routes.adminSettings,
      routes.adminIntegrations,
    ],
  },
  minimal: {
    label: "Tableau de bord + Pharmacie",
    hidden: [
      routes.admissions,
      routes.emergency,
      routes.appointments,
      routes.patients,
      routes.patientRecords,
      routes.consultations,
      routes.prescriptions,
      routes.laboratory,
      routes.labResults,
      routes.imaging,
      routes.imagingReports,
      routes.rhEmployees,
      routes.rhDepartments,
      routes.rhSchedules,
      routes.supplyMedicinesArticles,
      routes.supplyMedicinesLevels,
      routes.supplyMedicinesReceipts,
      routes.supplyConsumablesArticles,
      routes.supplyConsumablesLevels,
      routes.supplyConsumablesReceipts,
      routes.billingInvoices,
      routes.pharmacyInvoiceHistory,
      routes.adminUsers,
      routes.adminSettings,
      routes.adminIntegrations,
    ],
  },
} as const;
